import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { getAuth, OAuthProvider, signInWithCustomToken, updatePassword } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, getFirestore, updateDoc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { getFunctions, Functions } from 'firebase/functions';
import { SpotLockUser } from '../sp-types/userType';

const {
  VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID,
  VITE_GOOGLE_CLIENT_ID,
  //@ts-expect-error env is not found
} = import.meta.env;

const firebaseConfig = {
  apiKey: VITE_FIREBASE_API_KEY,
  authDomain: VITE_FIREBASE_AUTH_DOMAIN,
  projectId: VITE_FIREBASE_PROJECT_ID,
  storageBucket: VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: VITE_FIREBASE_APP_ID,
  clientId: VITE_GOOGLE_CLIENT_ID,
};

// Initialize Firebase
console.log('[Firebase] Initializing app...');
export const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
export const clientId = firebaseConfig.clientId;

console.log('[Firebase] Initializing services...');
export const db = getFirestore(app);
export const auth = getAuth(app);

console.log('[Firebase] Initializing functions for region: us-central1');
export const functions = getFunctions(app, 'us-central1');
console.log('[Firebase] ✅ Functions initialized:', functions);

export const companySSOProvider = new OAuthProvider('oidc.spotlock-supplier-sso');

// Initialize Analytics only if it's supported (optional, won't crash if not enabled)
let analytics;
try {
  analytics = getAnalytics(app);
} catch (error) {
  console.warn('Analytics not available:', error);
}
export { analytics };

export async function getBucketUrl(filepath: string) {
  const storage = getStorage();
  const gsReference = ref(storage, filepath);
  console.log(gsReference.fullPath);
  return gsReference.fullPath;
}

export async function getPDFbyUrl(filepath: string) {
  try {
    const storage = getStorage();
    const storageRef = ref(storage, filepath);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (err) {
    console.error('Error fetching PDF:', err);
  }
}

export async function uploadFile(file: any, prefix: string) {
  try {
    const storage = getStorage();
    const storageRef = ref(storage, `${prefix}/${file.name}`);

    // Create a promise to handle the upload
    return new Promise<string>((resolve, reject) => {
      // 'file' comes from the Blob or File API
      uploadBytes(storageRef, file)
        .then((snapshot) => {
          console.log(`Uploaded file: ${snapshot.ref.fullPath}`);
          resolve(snapshot.ref.fullPath); // Resolve the promise with the file path
        })
        .catch((error) => {
          console.error('Error uploading file:', error);
          reject(error); // Reject the promise in case of an error
        });
    });
  } catch (error) {
    console.error('Error in try-catch block:', error);
    throw error; // Rethrow the error for error handling at a higher level if needed
  }
}

export async function deleteFile(filePath: string) {
  try {
    const storage = getStorage();
    const fileRef = ref(storage, filePath);

    // Delete the file
    await deleteObject(fileRef);
    console.log(`File deleted successfully: ${filePath}`);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error; // Rethrow the error for error handling at a higher level if needed
  }
}

export function updateUserPassword(token: string, newPassword: string) {
  const auth = getAuth(app);
  signInWithCustomToken(auth, token)
    .then((userCredential) => {
      // Signed in
      const user = userCredential.user;
      if (user) {
        updatePassword(user, newPassword)
          .then(() => {
            // Update successful.
          })
          .catch((error) => {
            console.log(error);
          });
      }
      // ...
    })
    .catch((error) => {
      console.log(error);

      // ...
    });
}

export async function getUserDetails(id: string): Promise<SpotLockUser | null> {
  const docRef = doc(db, 'users', id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const spotLockUserData = docSnap.data();
    console.log(docSnap.id);
    spotLockUserData.id = docSnap.id;
    return spotLockUserData as SpotLockUser;
  }
  return null;
}

export async function updateOrderStatus(id: string, status: string) {
  try {
    const docRef = doc(db, 'orders', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      await updateDoc(doc(db, 'accounts', id), {
        status: status,
      });
    }
    console.log('Order status been set successfully');
  } catch (err) {
    console.log(`error found! ${err}`);
  }
}

export async function getQuotes(country: string) {
  const auth = getAuth(app);
  const user = auth.currentUser;
  console.log(user);
  const quotesCol = country === 'IL' ? collection(db, 'quotes') : collection(db, 'quotes-us');

  const quotesSnapshot = await getDocs(quotesCol);

  const quoteList = quotesSnapshot.docs.map((doc) => doc.data());
  return quoteList;
}
